/**
 * Utility functions for time calculations and conversions
 */

/**
 * Converts nextScheduledAnalysis value to milliseconds until the scheduled time
 *
 * @param nextScheduledAnalysis - Can be:
 *   - epoch timestamp (number) - if > 1e12, treated as timestamp; otherwise as duration in ms
 *   - ISO date string
 *   - duration in milliseconds
 * @returns milliseconds until the next scheduled analysis, or undefined if invalid/null
 */
export function getMillisecondsUntilNextAnalysis(
  nextScheduledAnalysis: string | number | null | undefined,
): number | undefined {
  if (nextScheduledAnalysis === undefined || nextScheduledAnalysis === null) {
    return undefined;
  }

  if (typeof nextScheduledAnalysis === 'number') {
    // If it's a big number (likely epoch ms), treat as timestamp; otherwise treat as duration ms.
    const EPOCH_MS_THRESHOLD = 1e12;
    if (nextScheduledAnalysis > EPOCH_MS_THRESHOLD) {
      return nextScheduledAnalysis - Date.now();
    } else {
      return nextScheduledAnalysis;
    }
  }

  // Try to parse as date string
  const parsed = Date.parse(nextScheduledAnalysis);
  if (!Number.isNaN(parsed)) {
    return parsed - Date.now();
  }

  return undefined;
}

/**
 * Checks if the next scheduled analysis is more than the specified minutes away
 *
 * @param nextScheduledAnalysis - The next scheduled analysis value
 * @param minutes - Number of minutes to check against
 * @returns true if more than the specified minutes away, false otherwise
 */
export function isAnalysisScheduledMoreThanMinutesAway(
  nextScheduledAnalysis: string | number | null | undefined,
  minutes: number,
): boolean {
  const msUntilNext = getMillisecondsUntilNextAnalysis(nextScheduledAnalysis);
  return msUntilNext !== undefined && msUntilNext > minutes * 60 * 1000;
}

const ANALYSIS_SCHEDULE_WARNING_MINUTES = 2;

/**
 * Checks if the next scheduled analysis is within the specified minutes from now
 *
 * @param nextScheduledAnalysis - The next scheduled analysis value
 * @param minutes - Number of minutes to check within
 * @returns true if within the specified minutes from now, false otherwise
 */
export function isAnalysisScheduledWithinMinutes(
  nextScheduledAnalysis: string | number | null | undefined,
  minutes: number = ANALYSIS_SCHEDULE_WARNING_MINUTES,
): boolean {
  const msUntilNext = getMillisecondsUntilNextAnalysis(nextScheduledAnalysis);
  return msUntilNext !== undefined && msUntilNext <= minutes * 60 * 1000;
}

/**
 * Formats a scheduled analysis time as a human-readable date string
 *
 * @param nextScheduledAnalysis - The next scheduled analysis value
 * @returns formatted date string like "on September 3 at 3:15 PM" or fallback text
 */
export function formatScheduledAnalysisDate(
  nextScheduledAnalysis: string | number | null | undefined,
): string {
  if (nextScheduledAnalysis === undefined || nextScheduledAnalysis === null) {
    return 'a future date';
  }

  let timestamp: number | undefined;

  if (typeof nextScheduledAnalysis === 'number') {
    const EPOCH_MS_THRESHOLD = 1e12;
    if (nextScheduledAnalysis > EPOCH_MS_THRESHOLD) {
      timestamp = nextScheduledAnalysis;
    } else {
      // If it's a duration, add to current time
      timestamp = Date.now() + nextScheduledAnalysis;
    }
  } else {
    // Try to parse as date string
    const parsed = Date.parse(nextScheduledAnalysis);
    if (!Number.isNaN(parsed)) {
      timestamp = parsed;
    }
  }

  if (timestamp === undefined) {
    return 'a future date';
  }

  const date = new Date(timestamp);

  // Format date and time separately to avoid parsing issues
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const datePart = date.toLocaleDateString('en-US', dateOptions);
  const timePart = date.toLocaleTimeString('en-US', timeOptions);

  return `${datePart} at ${timePart}`;
}
